import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { useToast } from '@/hooks/use-toast';
import { Clock } from 'lucide-react';

interface RateLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: () => void;
  isDarkMode: boolean;
  remainingQueries?: number;
}

export const RateLimitModal = ({ 
  isOpen, 
  onClose, 
  onAuthSuccess,
  isDarkMode, 
  remainingQueries = 0 
}: RateLimitModalProps) => {
  const [step, setStep] = useState<'rate-limit' | 'auth'>('rate-limit');
  const { signInWithGoogle } = useSupabase();
  const { toast } = useToast();

  const handleContinue = () => {
    setStep('auth');
  };
  
  const handleGoogleAuth = async () => {
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Successfully authenticated! You can now generate unlimited images.",
        });
        onAuthSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Google auth error:', error);
      toast({
        title: "Error",
        description: "Failed to authenticate with Google",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-md ${
        isDarkMode 
          ? 'bg-gray-900 border-gray-700 text-white' 
          : 'bg-white border-gray-200 text-gray-900'
      }`}>
        {step === 'rate-limit' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-center text-xl flex items-center justify-center gap-2">
                <Clock className="w-5 h-5" />
                Query Limit Reached
              </DialogTitle>
              <DialogDescription className={`text-center ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                You've used your free daily query limit ({remainingQueries} remaining).
                Sign in to continue with unlimited access.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className={`p-4 rounded-lg border ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <h3 className="font-semibold mb-2">Free Users</h3>
                <ul className={`text-sm space-y-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <li>• 1 image generation per day</li>
                  <li>• Basic features access</li>
                  <li>• No chat history saved</li>
                </ul>
              </div>
              
              <div className={`p-4 rounded-lg border-2 ${
                isDarkMode 
                  ? 'bg-blue-900/20 border-blue-600' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <h3 className="font-semibold mb-2 text-blue-600">Signed In Users</h3>
                <ul className={`text-sm space-y-1 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li>• Unlimited image generation</li>
                  <li>• Chat history saved</li>
                  <li>• Profile on generated images</li>
                  <li>• Advanced AI features</li>
                </ul>
              </div>

              <Button 
                onClick={handleContinue}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Continue to Sign In
              </Button>
              
              <Button 
                onClick={onClose}
                variant="ghost"
                className="w-full"
              >
                Maybe Later
              </Button>
            </div>
          </>
        )}

        {/* Captcha step removed */}

        {step === 'auth' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-center text-xl">
                Continue with Google
              </DialogTitle>
              <DialogDescription className={`text-center ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Sign in with your Google account to unlock unlimited access
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <Button
                onClick={handleGoogleAuth}
                className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};